import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from "react-router-dom";
import Joyride, { Step, CallBackProps, STATUS } from 'react-joyride';
import NLPSearch, { SuggestionItem } from '../components/NLPSearch';
import TermsAndConditionsModal from '../components/TermsAndConditionsModal';
import UploadNotificationModal, { UploadNotification } from '../components/modals/UploadNotificationModal';
import localStorageService from '../services/localStorageService';
import { AuthContext } from '../store/ContextStore';
import NlpSearchService from '../services/NlpSearchService';
import UpcomingMatchesService from '../services/matchService';
import AuthService from '../services/authService';
import api from '../utils/api';
import { TERMS_AGREED, TYPE_AHEAD_SUGGESTION } from '../constants/routes';
import Cookies from "js-cookie";


function Dashboard() {
  const { selectedSport, team, isModalClosed, user, setUser } = useContext(AuthContext);
  
  const [periodConfigData, setConfigData] = useState<any>();
  const [entity, setEntity] = useState<string>('Player');
  const [periodsList, setPeriodsList] = useState<string[]>([]);
  const [selectedPeriod, setPeriod] = useState<string>('');
  const [quarterList, setQuarterList] = useState<string[]>([]);
  const [selectedQuarter, setQuarter] = useState<string>('');
  
  const [searchString, setSearchString] = useState<string>('');
  // TYPE-AHEAD STATE
  const [suggestions, setSuggestions] = useState<SuggestionItem[]>([]);

  const [recentQueries, setRecentQueries] = useState<any[]>([]);
  const [loadingQueries, setLoadingQueries] = useState<boolean>(false);
  const [localFIlters, setLocalFilters] = useState({
    "selectedPeriod": selectedPeriod,
    "selectedQuarter": selectedQuarter,
    "entity": entity,
  });

  const [upcomingMatches, setUpcomingMatches] = useState<any[]>([]);
  const [loadingMatches, setLoadingMatches] = useState<boolean>(false);

  const [fullSchedule, setFullSchedule] = useState<any[]>([]);
  const [showSchedule, setShowSchedule] = useState(false);

  const [uploadNotifications, setUploadNotifications] = useState<UploadNotification[]>([]);
  const [isUploadNotificationOpen, setIsUploadNotificationOpen] = useState(false);
  const [notificationsChecked, setNotificationsChecked] = useState(false);


  const nlpSearchService = new NlpSearchService();
  const upcomingMatchesService = new UpcomingMatchesService();
  const authService = new AuthService();

  const navigate = useNavigate();

  useEffect(() => {
    const fetchSuggestions = async () => {
      // Safety Check
      if (!user?.username) return;

      if (searchString.length < 3) {
        setSuggestions([]);
        return;
      }

      try {
        const url = `${TYPE_AHEAD_SUGGESTION}/?sport_code=${selectedSport}&entity=${entity}&q=${encodeURIComponent(
          searchString
        )}&limit=10&user_id=${encodeURIComponent(user.username)}`;

        const response = await fetch(url);

        if (response.ok) {
          const data = await response.json();
          setSuggestions(data.suggestions || []);
        }
      } catch (error) {
        console.error("Error fetching type-ahead suggestions:", error);
      }
    };

    const debounceTimer = setTimeout(() => {
      fetchSuggestions();
    }, 0);

    return () => clearTimeout(debounceTimer);
  }, [searchString, selectedSport, entity, user]);


  useEffect(() => {
  const fetchMatches = async () => {
    if (!team?.code || !selectedSport) return; 

    setLoadingMatches(true);
    try {
      const matches = await upcomingMatchesService.getUpcomingMatches(
        team.code,
        selectedSport 
      );

      if (Array.isArray(matches)) {
        setUpcomingMatches(matches);
      } else {
        setUpcomingMatches([]);
        if (matches?.message) {
          // console.info(matches.message);
        }
      }
    } catch (error) {
      console.error("Error fetching upcoming matches:", error);
      setUpcomingMatches([]);
    } finally {
      setLoadingMatches(false);
    }
  };

  fetchMatches();
}, [team?.code, selectedSport]);

  useEffect(() => {
    const checkUploadNotifications = async () => {
      if (notificationsChecked) return;
      setNotificationsChecked(true);

      try {
        const response = await authService.getUploadNotifications();
        if (response?.status === 200 && response?.data?.notifications?.length > 0) {
          setUploadNotifications(response.data.notifications);
          setIsUploadNotificationOpen(true);
        }
      } catch (error) {
        console.error("Error fetching upload notifications:", error);
      }
    };

    checkUploadNotifications();
  }, [notificationsChecked]);


  const handleViewFullSchedule = () => {
    if (!team?.name) return;
    navigate(`/schedule/${team.name}/2025`); 
  };

  
  const [runTour, setRunTour] = useState<boolean>(false);
  const [tourSteps, setTourSteps] = useState<Step[]>([
    {
      target: '#tour-search-input', // Target the input field in NLPSearch
      content: selectedSport === "MFB"
      ? entity === "Player"
          ? "Start by typing [e.g. Players with rushing yards > 25]"
          : "Start by typing [e.g. Teams with rushing yards > 25]"
      : entity === "Player"
          ? "Start by typing [e.g. Players with field goals made > 25]"
          : "Start by typing [e.g. Teams with field goals made > 25]",
      disableBeacon: true,
      placement: 'top',
      styles: {
        tooltip: {
          borderRadius: '1rem'
        },
      },
      showSkipButton: false,
      hideCloseButton: true,
    },
    {
      target: '#search-btn-123', // Target the input field in NLPSearch
      content: 'Click Search to proceed.',
      disableBeacon: true,
      placement: 'top',
      styles: {
        tooltip: {
          borderRadius: '1rem'
        },
        tooltipContent: {
          padding: "1rem"
        },
      },
      hideCloseButton: true,
    },
  ]);


  const getDashboardTourCookieName = () => `dashboardTour_${user?.username}`;
  const getResultTourCookieName = () => `resultTour_${user?.username}`;

// Joyride callback
const handleJoyrideCallback = (data: CallBackProps) => {
  const { status } = data;

  // Use STATUS enum and includes for proper type narrowing
  if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status as any)) {
    setRunTour(false);
    Cookies.set(getDashboardTourCookieName(), "true", { expires: 365 });
  }
};

// Function to start the tour
const startTour = (forceRestart: boolean = false) => {
  if (forceRestart) {
    Cookies.remove(getDashboardTourCookieName());
    Cookies.remove(getResultTourCookieName());
    localStorage.setItem('searchTourCompleted', 'false');
    localStorage.removeItem('tourStep');
    setRunTour(false);

    window.setTimeout(() => {
      setRunTour(true);
    }, 0);
    return;
  }

  const dashboardTourCompleted = Cookies.get(getDashboardTourCookieName());
  if (!dashboardTourCompleted) {
    setRunTour(true);
  } else {
    // console.log("Dashboard tour already completed.");
  }
};

  const handleToggle = (entityVal: string) => {
    setEntity(entityVal);
  };

  function handleSearch(e?: React.FormEvent) { // Made event optional
    if (e) e.preventDefault();

    // Store that the search step is done, so ResultScreen knows to continue the tour
    localStorage.setItem('tourStep', 'navigateToResults');
    localStorageService.addSearchFilter({});
    localStorageService.addSortFields({});
    if (runTour) {
      localStorage.setItem("searchTourCompleted", "true");
    }
    navigate("/resultscreen");
  }

  useEffect(() => {
    setPeriod('');
    setQuarterList([]);
    setQuarter('');
    setSearchString('');
    setSuggestions([]); // Clear suggestions on sport change
  }, [selectedSport]);

  useEffect(() => {
  localStorage.setItem('searchTourCompleted', 'false');
  let filters = localStorageService.getNLPFilter();
  // if (filters.searchString) setSearchString(filters.searchString);
  if (filters.entity) setEntity(filters.entity);
  if (filters.selectedPeriod) setLocalFilters(prev => ({ ...prev, "selectedPeriod": filters.selectedPeriod }));
  if (filters.selectedQuarter) setLocalFilters(prev => ({ ...prev, "selectedQuarter": filters.selectedQuarter }));
  
  // Don't fetch queries here - let the entity/selectedSport useEffect handle it
}, []);
  
  const fetchRecentQueries = async () => {
    try {
      setLoadingQueries(true);
      const userQueries = await nlpSearchService.getRecentQueries(selectedSport, entity);
      
      const displayQueries = (userQueries || []).slice(0, 6);
      
      setRecentQueries(displayQueries);
    } catch (error) {
      console.error('Error fetching recent queries:', error);
    } finally {
      setLoadingQueries(false);
    }
  };

  useEffect(() => {
    if (periodConfigData) {
      if (periodConfigData[entity]?.[selectedPeriod]) {
        let quaters: any = Object.values(periodConfigData[entity][selectedPeriod]);
        setQuarterList(quaters);
        if (quaters.length > 0) {
          setQuarter(localFIlters.selectedQuarter || quaters[0]);
        }
      } else {
        setQuarterList([]);
        setQuarter('');
      }
    }
  }, [selectedPeriod, periodsList, entity, periodConfigData, localFIlters.selectedQuarter]);

  useEffect(() => {
    if (periodConfigData) {
      setQuarterList([]);
      setQuarter('');
      setPeriodsList([]);
      setPeriod('');
      const newPeriodsList = Object.keys(periodConfigData[entity] || {});
      setPeriodsList(newPeriodsList);
      if (newPeriodsList.length > 0) {
        setPeriod((localFIlters.selectedPeriod && newPeriodsList.includes(localFIlters.selectedPeriod)) ? localFIlters.selectedPeriod : newPeriodsList[0]);
      }
    }
  }, [periodConfigData, entity, localFIlters.selectedPeriod]);

  useEffect(() => {

    let tourTimeout: NodeJS.Timeout;
    if (isModalClosed) {

      tourTimeout = setTimeout(() => {
        if (isModalClosed && document.getElementById('tour-search-input')) {
          // console.log("Target #tour-search-input found. Starting tour.");
          startTour();
        } else {
          // console.error("Target #tour-search-input NOT found. Tour will not start correctly.");
        }
      }, 100);
      
    }
    return () => clearTimeout(tourTimeout);
  }, [isModalClosed]);

  useEffect(() => {
    setTourSteps([
      {
        target: '#tour-search-input', // Target the input field in NLPSearch
        content: selectedSport === "MFB"
        ? entity === "Player"
            ? "Start by typing [e.g. Players with rushing yards > 25]"
            : "Start by typing [e.g. Teams with rushing yards > 25]"
        : entity === "Player"
            ? "Start by typing [e.g. Players with field goals made > 25]"
            : "Start by typing [e.g. Teams with field goals made > 25]",
        disableBeacon: true,
        placement: 'top',
        styles: {
          tooltip: {
            borderRadius: '1rem'
          },
        },
        showSkipButton: false,
        hideCloseButton: true,
      },
      {
        target: '#search-btn-123', // Target the input field in NLPSearch
        content: 'Click Search to proceed.',
        disableBeacon: true,
        placement: 'top',
        styles: {
          tooltip: {
            borderRadius: '1rem'
          },
          tooltipContent: {
            padding: "1rem"
          },
        },
        hideCloseButton: true,
      },
    ])
  }, [selectedSport, entity]);

  // Re-fetch queries when entity or sport changes to get sport/entity-specific queries
  useEffect(() => {
    fetchRecentQueries();
  }, [entity, selectedSport]);


  return (
    <> {/* Use Fragment to wrap Joyride and your existing JSX */}

      <Joyride
        steps={tourSteps}
        run={runTour}
        continuous={true}
        showProgress={true}
        showSkipButton={true}
        callback={handleJoyrideCallback}
        styles={{
          options: {
            zIndex: 10000,
            primaryColor: team?.colorCode || '#00B2B2',
          },
          
        }}
        spotlightClicks={true}
        // disableOverlay={true}
        debug={true}
        locale={{
    last: "Finish",  // This changes the "Last" text to "Finish"
    skip: "Skip"
  }}
      />

      <UploadNotificationModal
        isOpen={isUploadNotificationOpen}
        notifications={uploadNotifications}
        onAcknowledge={async () => {
          for (const notification of uploadNotifications) {
            try {
              await authService.markNotificationSeen(notification.id);
            } catch (error) {
              console.error("Error marking notification as seen:", error);
            }
          }
          setIsUploadNotificationOpen(false);
          setUploadNotifications([]);
        }}
      />

      <div className="w-full">
        <div className="px-4 sm:px-6 pt-4 sm:pt-6 flex flex-col items-center gap-6 sm:gap-9 w-full h-full">
          
          <div className='bg-white p-1.5 rounded-md w-full sm:w-[260px] max-w-[400px]'>
            <div className="relative flex gap-2 w-full">
              <div
                className={`rounded-md absolute top-0 left-0 h-full w-1/2 transition-transform duration-300 ${entity === "Player" ? "translate-x-0" : "translate-x-full"
                  }`}
                style={{
                  backgroundColor: team.colorCode,
                }}
              ></div>
              <div
                onClick={() => handleToggle("Player")}
                className={`relative z-10 flex flex-1 flex-row justify-center items-center p-2 sm:p-1 text-center cursor-pointer gap-1 sm:gap-0`}
                style={{
                  color: entity === "Player" ? "white" : team.colorCode
                }}>
                <svg className="w-4 h-4 sm:w-4 sm:h-4" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8.00033 0.666992C8.97279 0.666992 9.90542 1.0533 10.5931 1.74093C11.2807 2.42857 11.667 3.3612 11.667 4.33366C11.667 5.30612 11.2807 6.23875 10.5931 6.92638C9.90542 7.61402 8.97279 8.00033 8.00033 8.00033C7.02786 8.00033 6.09523 7.61402 5.4076 6.92638C4.71997 6.23875 4.33366 5.30612 4.33366 4.33366C4.33366 3.3612 4.71997 2.42857 5.4076 1.74093C6.09523 1.0533 7.02786 0.666992 8.00033 0.666992ZM8.00033 9.83366C12.052 9.83366 15.3337 11.4745 15.3337 13.5003V15.3337H0.666992V13.5003C0.666992 11.4745 3.94866 9.83366 8.00033 9.83366Z" fill={`${entity === "Team" ? team.colorCode : "white"}`} />
                </svg>
                <p className="text-sm sm:text-base">Player</p>
              </div>
              <div
                onClick={() => handleToggle("Team")}
                className={`relative z-10 flex flex-1 flex-row justify-center items-center p-2 sm:p-1 text-center cursor-pointer gap-1 sm:gap-0`}
                style={{
                  color: entity === "Team" ? "white" : team.colorCode
                }}
              >
                <svg className="w-5 h-4 sm:w-5 sm:h-4" viewBox="0 0 20 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10.9163 9.91667C11.8888 9.91667 12.8214 10.303 13.5091 10.9906C14.1967 11.6782 14.583 12.6109 14.583 13.5833V14.9583C14.583 15.323 14.4381 15.6727 14.1803 15.9306C13.9224 16.1885 13.5727 16.3333 13.208 16.3333H2.20801C1.84334 16.3333 1.4936 16.1885 1.23574 15.9306C0.977874 15.6727 0.833008 15.323 0.833008 14.9583V13.5833C0.833008 12.6109 1.21932 11.6782 1.90695 10.9906C2.59458 10.303 3.52721 9.91667 4.49967 9.91667H10.9163ZM16.4163 9.91667C17.1457 9.91667 17.8452 10.2064 18.3609 10.7221C18.8766 11.2378 19.1663 11.9373 19.1663 12.6667V14.0417C19.1663 14.4063 19.0215 14.7561 18.7636 15.0139C18.5058 15.2718 18.156 15.4167 17.7913 15.4167H16.4163V13.5833C16.4163 12.8718 16.2507 12.17 15.9325 11.5336C15.6143 10.8972 15.1522 10.3436 14.583 9.91667H16.4163ZM7.70801 0.75C8.80203 0.75 9.85124 1.1846 10.6248 1.95818C11.3984 2.73177 11.833 3.78098 11.833 4.875C11.833 5.96902 11.3984 7.01823 10.6248 7.79181C9.85124 8.5654 8.80203 9 7.70801 9C6.61399 9 5.56478 8.5654 4.79119 7.79181C4.0176 7.01823 3.58301 5.96902 3.58301 4.875C3.58301 3.78098 4.0176 2.73177 4.79119 1.95818C5.56478 1.1846 6.61399 0.75 7.70801 0.75ZM15.4997 3.5C16.229 3.5 16.9285 3.78973 17.4442 4.30546C17.9599 4.82118 18.2497 5.52065 18.2497 6.25C18.2497 6.97934 17.9599 7.67882 17.4442 8.19454C16.9285 8.71027 16.229 9 15.4997 9C14.7703 9 14.0709 8.71027 13.5551 8.19454C13.0394 7.67882 12.7497 6.97934 12.7497 6.25C12.7497 5.52065 13.0394 4.82118 13.5551 4.30546C14.0709 3.78973 14.7703 3.5 15.4997 3.5Z" fill={`${entity === "Team" ? "white" : team.colorCode}`} />
                </svg>
                <p className="text-sm sm:text-base">Team</p>
              </div>
            </div>
          </div>
          <div className='flex flex-row gap-2 items-center justify-center w-full h-[60px] max-w-[1024px] px-2 sm:px-0' id="tour-search-input">
    
            <NLPSearch filterdata={
              {
                "selectedPeriod": selectedPeriod,
                "selectedQuarter": selectedQuarter,
                "entity": entity,
                "sport": selectedSport
              }}
              searchString={searchString}
              setSearchString={setSearchString}
              handleSearch={(e: React.FormEvent) => handleSearch(e)} // Ensure NLPSearch calls this
              resetFilters={() => ({})}
              resetSorting={() => ({})}
              
              // 5. PASS NEW PROPS
              suggestions={suggestions}
              onSelectSuggestion={(val: string) => {
                  setSearchString(val);
                  setSuggestions([]);
              }}
            />
            
          </div>
          <button 
              className="text-white px-4 py-2 rounded-lg mt-4"
              style={{ backgroundColor: team.colorCode }}
              onClick={() => startTour(true)}
              >
                Visit tutorial
              </button>
          
          {/* Recent Queries Section */}
          <div className="w-full max-w-[1024px] mt-4 sm:mt-6 mb-6 sm:mb-8 px-2 sm:px-0">
            <h2 className="text-lg sm:text-xl font-semibold mb-4 sm:mb-5 text-gray-800">Recent Searches</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
              {loadingQueries ? (
                <div className="col-span-full flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: team.colorCode }}></div>
                </div>
              ) : recentQueries.length > 0 ? (
                recentQueries.map((query, index) => (
                  <div 
                    key={query.id || index}
                    className={`bg-white rounded-lg shadow-md p-4 sm:p-5 hover:shadow-lg transition-shadow cursor-pointer border ${
                      query.example ? 'border-gray-200' : 'border-gray-100'
                    }`}
                    onClick={() => {
                      setSearchString(query.query);
                    }}
                    style={{ minHeight: '90px', display: 'flex', alignItems: 'center' }}
                  >
                    <div className="w-full">
                      <p className="font-medium text-gray-800 text-sm sm:text-base" style={{ lineHeight: '1.5' }}>
                        {query.query}
                      </p>
                      
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-full text-center py-8 text-gray-500">
                  <p className="text-sm sm:text-base">No recent queries found. Start searching to see your history!</p>
                </div>
              )}
            </div>
          </div>

{/* Upcoming Matches Section */}
<div className="w-full max-w-[1024px] mt-6 sm:mt-8 mb-6 sm:mb-8 px-2 sm:px-0">
  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 mb-4 sm:mb-5">
    <h2 className="text-lg sm:text-xl font-semibold text-gray-800">
      Upcoming Games
    </h2>
    <span className="hidden sm:inline text-gray-400">|</span>
    <button
      onClick={handleViewFullSchedule}
      className="text-blue-600 hover:underline text-sm"
    >
      View Full Schedule
    </button>
  </div>

  {loadingMatches ? (
    <div className="flex justify-center py-8">
      <div
        className="animate-spin rounded-full h-8 w-8 border-b-2"
        style={{ borderColor: team.colorCode }}
      ></div>
    </div>
  ) : upcomingMatches.length > 0 ? (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
      {upcomingMatches.map((match, index) => (
        <div
          key={index}
          className="bg-white rounded-lg shadow-md p-4 sm:p-5 hover:shadow-lg transition-shadow flex items-center gap-3"
        >
          {/* Opponent logo */}
          <img
            src={match.opponent_logo}
            alt={match.opponent_team_name}
            className="w-10 h-10 sm:w-12 sm:h-12 object-contain rounded-full flex-shrink-0"
          />

          {/* Opponent info */}
          <div className="flex-1 min-w-0">
            <p className="font-medium text-gray-800 text-sm sm:text-base truncate">
              {match.home === 1
                ? `vs ${match.opponent_team_name}`
                : `at ${match.opponent_team_name}`}
            </p>

            <p className="text-xs sm:text-sm text-gray-500">
              {new Date(match.match_time).toLocaleDateString(undefined, {
                weekday: "short",
                month: "short",
                day: "numeric",
              })}{" "}
              {new Date(match.match_time).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })} UTC
            </p>

            {/* Venue name */}
            <p className="text-xs text-gray-400 truncate">{match.venue_name}</p>
          </div>
        </div>
      ))}
    </div>
  ) : (
    <div className="text-center py-8 text-gray-500">
      <p className="text-sm sm:text-base">No upcoming matches found.</p>
    </div>
  )}
</div>
        </div>

      </div>
    </>
  );
}

export default Dashboard;
